import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { config } from "../config/environment";
import { log, handleApiError } from "../utils/helpers";

interface CategoryFormProps {
  initialData?: {
    name: string;
    slug: string;
    description: string;
  };
}

const CategoryForm: React.FC<CategoryFormProps> = ({ initialData }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    ...initialData,
  });

  useEffect(() => {
    if (id) {
      fetchCategory();
    }
  }, [id]);

  const fetchCategory = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/categories/${id}`);
      const data = await response.json();
      setFormData(data);
      log("Category fetched successfully", "debug", { categoryId: id });
    } catch (error) {
      const errorMessage = handleApiError(error);
      log("Error fetching category", "error", {
        error: errorMessage,
        categoryId: id,
      });
      console.error("Error fetching category:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "name"
        ? { slug: value.toLowerCase().replace(/\s+/g, "-") }
        : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = id
        ? `${config.api.baseUrl}/categories/${id}`
        : `${config.api.baseUrl}/categories`;
      const method = id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to save category");
      }

      navigate("/categories");
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {id ? "Edit Category" : "Add Category"}
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />

          <TextField
            fullWidth
            label="Slug"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            required
            helperText="Auto-generated from name, but you can modify it"
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={4}
          />

          <Button type="submit" variant="contained" color="primary" fullWidth>
            {id ? "Update Category" : "Create Category"}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};

export default CategoryForm;
